import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from "../lib/supabase";
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return setStatus({ type: 'error', msg: t('auth.update_password.error_match') });
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: password });
      if (error) throw error;

      setStatus({ type: 'success', msg: t('auth.update_password.success') });
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setStatus({ type: 'error', msg: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 border border-border">
        <div className="p-8">
          <h2 className="text-xl font-black uppercase tracking-widest mb-2">
            {t('auth.update_password.title')}
          </h2>
          <p className="text-muted-foreground text-[11px] font-bold uppercase tracking-wider mb-8">
            {t('auth.update_password.desc')}
          </p>

          {status.msg && (
            <div className={`mb-6 p-4 rounded-2xl border ${
              status.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-red-50 border-red-100 text-red-500'
            }`}>
              <p className="text-xs text-center font-bold uppercase">{status.msg}</p>
            </div>
          )}

          <form onSubmit={handleUpdatePassword} className="space-y-6">

            <div className="relative">
              <Input 
                type={showPassword ? 'text' : 'password'}
                label={t('auth.update_password.new_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            <div className="relative">
              <Input 
                type={showConfirmPassword ? 'text' : 'password'}
                label={t('auth.update_password.confirm_password')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword(p => !p)}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            <div className="pt-4">
              <Button type="submit" fullWidth loading={isLoading}>
                {t('auth.update_password.button')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;